import { useState, useEffect } from "react";
import { GraduationCap, Copy, Check, WhatsappLogo, XLogo, EnvelopeSimple, LinkSimple, Gift, UsersThree, CalendarPlus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const BASE_URL = "https://marketpilot365.com";
const HEADLINE = "Learn to trade the smart way — with MarketPilot AI 📈";

function buildNote(link, trialDays) {
  return `If you want to learn trading without risking real money, try MarketPilot AI:

• ${trialDays}-day full-access trial (my invite gives you extra days) to practice risk-free
• $100K paper-trading account
• AI scores & probabilistic forecasts that teach you to think in odds, not hype
• A plain-English AI assistant that explains the "why" behind every signal
• Market scanner, trade ideas, live alerts & a forecast scorecard

Start free 👉 ${link}

(Not financial advice — everything is probabilities and educational tools. Always do your own research.)`;
}

export default function Invite() {
  const [copied, setCopied] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/referral").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const code = data?.code;
  const rewardDays = data?.reward_days ?? 30;
  const trialDays = data?.referred_trial_days ?? 21;
  const link = code ? `${BASE_URL}/?ref=${code}` : BASE_URL;
  const NOTE = buildNote(link, trialDays);
  const FULL = `${HEADLINE}\n\n${NOTE}`;
  const TWEET = `Learning to trade? I'm using MarketPilot AI — a full-access trial, a $100K paper account + AI scores & probabilistic forecasts that teach you to think in odds, not hype. Grab extra days with my link 👉 ${link}`;

  const copy = async (text, key, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      toast.success(label);
      setTimeout(() => setCopied(""), 2000);
    } catch {
      toast.error("Couldn't copy — please select and copy manually.");
    }
  };

  const stats = [
    { key: "joined", icon: UsersThree, label: "Friends joined", value: data?.joined ?? 0 },
    { key: "converted", icon: Gift, label: "Subscribed", value: data?.converted ?? 0 },
    { key: "bonus", icon: CalendarPlus, label: "Bonus days earned", value: data?.bonus_days_earned ?? 0 },
  ];

  const shares = [
    { key: "whatsapp", label: "WhatsApp", icon: WhatsappLogo, cls: "hover:border-signal-green/50 hover:text-signal-green",
      href: `https://wa.me/?text=${encodeURIComponent(FULL)}` },
    { key: "x", label: "Post on X", icon: XLogo, cls: "hover:border-white/60 hover:text-white",
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(TWEET)}` },
    { key: "email", label: "Email", icon: EnvelopeSimple, cls: "hover:border-volt/60 hover:text-volt",
      href: `mailto:?subject=${encodeURIComponent("A smart, risk-free way to learn trading")}&body=${encodeURIComponent(NOTE)}` },
  ];

  return (
    <div className="max-w-2xl mx-auto" data-testid="invite-page">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center bg-volt/15 border border-volt/30 rounded-sm">
          <GraduationCap size={22} className="text-volt" />
        </div>
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Invite friends, earn free access</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Share MarketPilot AI — anyone who joins gets a longer trial, and you earn bonus days when they subscribe.</p>
        </div>
      </div>

      {/* Reward explainer */}
      <div className="mt-6 flex items-start gap-3 border border-signal-green/30 bg-signal-green/[0.06] rounded-sm p-4" data-testid="referral-reward-banner">
        <Gift size={20} weight="fill" className="text-signal-green shrink-0 mt-0.5" />
        <p className="text-sm text-foreground">
          <span className="font-medium">Earn +{rewardDays} days of full access</span>
          <span className="text-muted-foreground"> for every friend who subscribes to a paid plan. Friends who sign up with your link start with a </span>
          <span className="font-medium">{trialDays}-day trial</span>
          <span className="text-muted-foreground"> instead of 14.</span>
        </p>
      </div>

      {/* Stats */}
      <div className="mt-4 grid grid-cols-3 gap-3" data-testid="referral-stats">
        {stats.map((s) => (
          <div key={s.key} className="border border-border bg-card p-4" data-testid={`referral-stat-${s.key}`}>
            <s.icon size={18} className="text-volt" />
            <div className="mt-2 mono text-2xl font-semibold">{s.value}</div>
            <div className="label-xs mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Referral link */}
      <div className="mt-6">
        <h2 className="label-xs mb-2">Your invite link</h2>
        <div className="flex items-center gap-2">
          <div className="flex-1 flex items-center gap-2 border border-border bg-card rounded-sm px-3 h-11 min-w-0">
            <LinkSimple size={16} className="text-muted-foreground shrink-0" />
            <span className="mono text-sm truncate" data-testid="invite-link">{link}</span>
          </div>
          <Button onClick={() => copy(link, "link", "Invite link copied")} data-testid="invite-copy-link"
            className="h-11 bg-volt hover:bg-volt/90 text-white rounded-sm shrink-0">
            <Copy size={16} className="mr-1.5" /> Copy link
          </Button>
        </div>
      </div>

      {/* Share message */}
      <div className="mt-6 border border-border bg-card rounded-sm overflow-hidden" data-testid="invite-preview">
        <div className="border-b border-border px-5 py-3 flex items-center justify-between">
          <span className="label-xs">Share message</span>
          <button onClick={() => copy(FULL, "note", "Message copied")} data-testid="invite-copy-note"
            className="inline-flex items-center gap-1.5 mono text-[11px] text-muted-foreground hover:text-volt transition-colors duration-200">
            {copied === "note" ? <Check size={13} className="text-signal-green" /> : <Copy size={13} />}
            {copied === "note" ? "Copied" : "Copy"}
          </button>
        </div>
        <div className="px-5 py-5">
          <p className="font-heading font-semibold">{HEADLINE}</p>
          <pre className="mt-3 whitespace-pre-wrap font-sans text-sm text-muted-foreground leading-relaxed">{NOTE}</pre>
        </div>
      </div>

      {/* Share buttons */}
      <div className="mt-6">
        <h2 className="label-xs mb-2">Share via</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {shares.map((s) => (
            <a key={s.key} href={s.href} target="_blank" rel="noreferrer" data-testid={`invite-share-${s.key}`}
              className={`flex items-center justify-center gap-2 border border-border bg-card rounded-sm py-3 text-sm text-muted-foreground transition-colors duration-200 ${s.cls}`}>
              <s.icon size={18} weight="fill" /> {s.label}
            </a>
          ))}
        </div>
      </div>

      <p className="mt-8 text-xs text-muted-foreground/70 leading-relaxed">
        MarketPilot AI is an educational and analytics platform. Nothing shared here is financial advice — all outputs are probabilities and learning tools. Bonus days are credited automatically once a referred friend completes a paid subscription.
      </p>
    </div>
  );
}
